/* ──────────────────────────────────────────────────────────────────────────
 * Apollo Bearing Index — motion helpers (IFX.motion)
 * assets/interchange-motion.js
 *
 * Every effect checks IFX.motionOK() (prefers-reduced-motion) and degrades
 * to the final state instantly.
 *
 *   motion.reveal(container)            staggered card reveal (one IntersectionObserver, cap 12)
 *   motion.countUp(el, target, opts)    number tween on first intersection
 *   motion.tween(from, to, ms, onFrame) rAF tween with ease-out, returns cancel()
 *   motion.flip(indicator, target)      slide the tab indicator under `target`
 *   motion.bearing(svg)                 pause the hero drawing off-screen / hidden tab
 *   motion.model(el)                    hero <model-viewer>: load the library on demand, auto-rotate rules, fallback
 *   motion.sticky(sentinel, onChange)   sticky bar trigger (hero scrolled out)
 *   motion.morphNumber(el, value, fmt)  numbers that "roll" on unit toggles
 * ───────────────────────────────────────────────────────────────────────── */
(function () {
  "use strict";
  var IFX = window.IFX;
  if (!IFX || IFX.motion) return;

  var REVEAL_CAP = 12;

  function easeOut(t) { return 1 - Math.pow(1 - t, 3); }

  function tween(from, to, ms, onFrame, onDone) {
    if (!IFX.motionOK() || ms <= 0) { onFrame(to, 1); if (onDone) onDone(); return function () {}; }
    var start = null, raf = null, cancelled = false;
    function step(ts) {
      if (cancelled) return;
      if (start == null) start = ts;
      var t = Math.min(1, (ts - start) / ms);
      onFrame(from + (to - from) * easeOut(t), t);
      if (t < 1) raf = requestAnimationFrame(step);
      else if (onDone) onDone();
    }
    raf = requestAnimationFrame(step);
    return function () { cancelled = true; if (raf) cancelAnimationFrame(raf); };
  }

  /* Staggered reveal: children with [data-reveal] get is-visible when they
     enter the viewport; --ifx-i drives the CSS delay, capped so a long list
     never waits on itself. */
  var revealObserver = null;
  function getRevealObserver() {
    if (revealObserver || typeof IntersectionObserver !== "function") return revealObserver;
    revealObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-visible");
        revealObserver.unobserve(entry.target);
      });
    }, { rootMargin: "0px 0px -8% 0px", threshold: 0.05 });
    return revealObserver;
  }
  var REVEAL_FALLBACK_MS = 1000;
  function reveal(container) {
    var items = container.querySelectorAll("[data-reveal]");
    var obs = IFX.motionOK() ? getRevealObserver() : null;
    var i = 0;
    items.forEach(function (el) {
      if (el.classList.contains("is-visible")) return;
      el.style.setProperty("--ifx-i", String(Math.min(i, REVEAL_CAP)));
      i += 1;
      if (obs) obs.observe(el);
      else el.classList.add("is-visible");
    });
    // Safety net: anything the observer has not reached after a second
    // (print, screenshot tools, a hidden tab) becomes visible anyway; the
    // stagger only ever applies to what is on screen.
    if (obs) {
      setTimeout(function () {
        items.forEach(function (el) {
          if (!el.classList.contains("is-visible")) { el.classList.add("is-visible"); obs.unobserve(el); }
        });
      }, REVEAL_FALLBACK_MS);
    }
  }

  /* Count-up on first sight. `target` may be a number or a "125,000+" string
     (digits are tweened, the suffix is kept). */
  var countObserver = null;
  var countQueue = new Map();
  function parseTarget(target) {
    var s = String(target);
    var m = /^([^0-9]*)([0-9][0-9,.]*)(.*)$/.exec(s);
    if (!m) return null;
    return { prefix: m[1], value: Number(m[2].replace(/,/g, "")), suffix: m[3] };
  }
  function renderCount(el, parts, value) {
    var v = Math.round(value);
    el.textContent = parts.prefix + IFX.fmt.num(v) + parts.suffix;
  }
  function countUp(el, target, opts) {
    opts = opts || {};
    var parts = parseTarget(target);
    if (!parts || !Number.isFinite(parts.value)) { el.textContent = String(target); return; }
    var run = function () {
      var from = opts.from != null ? opts.from : Math.max(0, parts.value * 0.6);
      tween(from, parts.value, opts.duration || 900, function (v) { renderCount(el, parts, v); });
    };
    if (!IFX.motionOK() || typeof IntersectionObserver !== "function") { renderCount(el, parts, parts.value); return; }
    if (!countObserver) {
      countObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var fn = countQueue.get(entry.target);
          countQueue.delete(entry.target);
          countObserver.unobserve(entry.target);
          if (fn) fn();
        });
      }, { threshold: 0.4 });
    }
    countQueue.set(el, run);
    countObserver.observe(el);
  }

  /* FLIP-style sliding indicator for the segmented tab bar. */
  function flip(indicator, target) {
    if (!indicator || !target) return;
    var parent = indicator.parentElement;
    var pr = parent.getBoundingClientRect();
    var tr = target.getBoundingClientRect();
    var x = tr.left - pr.left + parent.scrollLeft;
    indicator.style.width = tr.width + "px";
    indicator.style.transform = "translateX(" + x + "px)";
    indicator.classList.add("is-ready");
  }

  /* Hero bearing: CSS animations run only while on screen and the tab is
     visible; the class toggle is what CSS keys off. */
  function bearing(svg) {
    if (!svg) return;
    var onScreen = true;
    function sync() {
      var run = IFX.motionOK() && onScreen && !document.hidden;
      svg.classList.toggle("is-running", run);
    }
    if (typeof IntersectionObserver === "function") {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { onScreen = e.isIntersecting; });
        sync();
      }, { threshold: 0.1 }).observe(svg);
    }
    document.addEventListener("visibilitychange", sync);
    sync();
    // One orchestrated moment on load: the rings draw in.
    if (IFX.motionOK()) {
      requestAnimationFrame(function () { svg.classList.add("is-drawn"); });
    } else {
      svg.classList.add("is-drawn");
    }
  }

  /* Hero 3D model: the <model-viewer> library (module script, once) is fetched
     only when the figure comes near the viewport, so phones (where the art is
     display:none) never pay for it. Auto-rotate follows the drawing's rules:
     motion allowed, on screen, tab visible. A load error puts the drawing back. */
  var MODEL_VIEWER_SRC = "https://ajax.googleapis.com/ajax/libs/model-viewer/3.5.0/model-viewer.min.js";
  var modelLibrary = null;
  function loadModelViewer() {
    if (window.customElements && customElements.get("model-viewer")) return Promise.resolve();
    if (modelLibrary) return modelLibrary;
    modelLibrary = new Promise(function (resolve, reject) {
      if (!window.customElements) { reject(new Error("no custom elements")); return; }
      var s = document.createElement("script");
      s.type = "module"; s.src = MODEL_VIEWER_SRC; s.crossOrigin = "anonymous";
      s.onload = function () { customElements.whenDefined("model-viewer").then(resolve, reject); };
      s.onerror = function () { reject(new Error("model-viewer failed to load")); };
      document.head.appendChild(s);
    });
    return modelLibrary;
  }
  function model(el) {
    if (!el) return;
    var figure = el.closest(".ifx-hero__art") || el.parentNode;
    var onScreen = false, ready = false, failed = false;
    function sync() {
      if (!ready || failed) return;
      el.autoRotate = IFX.motionOK() && onScreen && !document.hidden;
    }
    function fail() {
      if (failed) return;
      failed = true;
      var poster = el.querySelector("[slot=poster]");
      if (poster) { while (poster.firstChild) figure.insertBefore(poster.firstChild, el); }
      var hint = figure.querySelector(".ifx-model__hint");
      if (hint) hint.remove();
      el.remove();
      figure.classList.remove("ifx-hero__art--model", "is-loaded");
      figure.setAttribute("aria-hidden", "true");
    }
    function boot() {
      if (ready || failed) return;
      loadModelViewer().then(function () {
        ready = true;
        el.addEventListener("load", function () { figure.classList.add("is-loaded"); sync(); });
        el.addEventListener("error", fail);
        sync();
      }, fail);
    }
    if (typeof IntersectionObserver === "function") {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { onScreen = e.isIntersecting; });
        if (onScreen) boot();
        sync();
      }, { rootMargin: "200px 0px", threshold: 0 }).observe(el);
    } else {
      onScreen = true; boot();
    }
    document.addEventListener("visibilitychange", sync);
  }

  /* Sticky compact bar: fires onChange(true) once the sentinel (the hero) has
     scrolled above the theme header. */
  function sticky(sentinel, onChange, headerOffset) {
    if (!sentinel || typeof IntersectionObserver !== "function") return;
    var off = Math.max(0, Number(headerOffset) || 0);
    new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        onChange(!e.isIntersecting && e.boundingClientRect.top < 0);
      });
    }, { rootMargin: "-" + off + "px 0px 0px 0px", threshold: 0 }).observe(sentinel);
  }

  /* Numbers that roll from the old value to the new one (unit toggles). */
  function morphNumber(el, value, format, ms) {
    var prev = Number(el.dataset.ifxValue);
    var next = Number(value);
    el.dataset.ifxValue = String(next);
    if (!Number.isFinite(prev) || !Number.isFinite(next) || !IFX.motionOK()) { el.textContent = format(next); return; }
    tween(prev, next, ms || 320, function (v, t) { el.textContent = format(t >= 1 ? next : v); });
  }

  var motion = { tween: tween, reveal: reveal, countUp: countUp, flip: flip, bearing: bearing, model: model, sticky: sticky, morphNumber: morphNumber };
  IFX.motion = motion;
  IFX.define("motion", motion);
})();
